import { useEffect } from 'react';
import { BalanceType } from '@weave/schema-gen-ts/dist/schemas/payments/messages/commands/commands.pb';
import { useTranslation } from '@frontend/i18n';
import { CheckboxField, ModalControlModalProps, TableFilters, useForm } from '@frontend/design-system';
import { useBulkCollectionsFilterShallowStore } from '../../../../../store/bulk-collections-filter-store';
import { BalanceTag } from '../../../common/BalanceTag';
import { styles } from '../../styles';

type TableFiltersProps = {
  modalProps: ModalControlModalProps;
  setShowNotification: (show: boolean) => void;
};
export const FilterPanel = ({ modalProps, setShowNotification }: TableFiltersProps) => {
  const { t } = useTranslation('payments');
  const { setBalanceType, balanceType, clearFilters } = useBulkCollectionsFilterShallowStore(
    'balanceType',
    'setBalanceType',
    'clearFilters'
  );

  const { getFieldProps, values, seedValues } = useForm({
    fields: {
      newBalance: { type: 'checkbox', value: balanceType.includes(BalanceType.BALANCE_TYPE_NEW) },
      changedBalance: { type: 'checkbox', value: balanceType.includes(BalanceType.BALANCE_TYPE_UPDATE) },
      existingBalance: { type: 'checkbox', value: balanceType.includes(BalanceType.BALANCE_TYPE_EXISTS) },
    },
  });

  const handleApply = () => {
    const selectedBalanceTypes = [
      values.newBalance && BalanceType.BALANCE_TYPE_NEW,
      values.changedBalance && BalanceType.BALANCE_TYPE_UPDATE,
      values.existingBalance && BalanceType.BALANCE_TYPE_EXISTS,
    ].filter(Boolean) as BalanceType[];
    setBalanceType(selectedBalanceTypes);
  };

  const handleClear = () => {
    clearFilters();
    modalProps.onClose();
  };

  useEffect(() => {
    if (modalProps.show) {
      seedValues({
        newBalance: balanceType.includes(BalanceType.BALANCE_TYPE_NEW),
        changedBalance: balanceType.includes(BalanceType.BALANCE_TYPE_UPDATE),
        existingBalance: balanceType.includes(BalanceType.BALANCE_TYPE_EXISTS),
      });
    }
  }, [modalProps.show]);

  useEffect(() => {
    if (balanceType.length === 0) {
      setShowNotification(false);
    } else {
      setShowNotification(true);
    }
  }, [balanceType]);

  return (
    <TableFilters
      {...modalProps}
      trackingId='collection-table-filter'
      onApplyClick={handleApply}
      headerLabel={t('Filters')}
      onClearAllClick={handleClear}
      clearAllLabel={t('Clear All')}
    >
      <TableFilters.Section
        sectionHeaderLabel={t('Collection Status')}
        disableClear={!values.newBalance && !values.changedBalance && !values.existingBalance}
        onClearClick={() => seedValues({ newBalance: false, changedBalance: false, existingBalance: false })}
        data-trackingid={`collections-balance-type-filter`}
      >
        <div css={styles.filterPanel.checkboxList}>
          <CheckboxField
            {...getFieldProps('newBalance')}
            label={<BalanceTag balanceType={BalanceType.BALANCE_TYPE_NEW} maxWidth='300px' />}
          />
          <CheckboxField
            {...getFieldProps('changedBalance')}
            label={<BalanceTag balanceType={BalanceType.BALANCE_TYPE_UPDATE} maxWidth='300px' />}
          />
          <CheckboxField
            {...getFieldProps('existingBalance')}
            label={<BalanceTag balanceType={BalanceType.BALANCE_TYPE_EXISTS} maxWidth='300px' />}
          />
        </div>
      </TableFilters.Section>
    </TableFilters>
  );
};
